/**
 * Apply agent / WebSocket wishlist actions to WishlistContext (local state only).
 * Used by typed chat, voice/avatar, and any OrbieeFlow caller — no REST API.
 */

import { flyToBag, productImageOf } from "./flyToBag";
import { showToast } from "./toastManager";

const WISHLIST_SYNC_KEY = "orbiee_wishlist_synced_at";

export function stampWishlistAgentSync() {
  try {
    sessionStorage.setItem(WISHLIST_SYNC_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
}

/**
 * Apply a turn's wishlist outcome to local state.
 *
 * State only — this function never navigates and never touches the try-on
 * overlay. OrbieeFlow owns both: it sees every intent in the turn, so it can
 * decide whether /wishlist is really where the shopper should land when the
 * same turn also searched or opened a product.
 *
 * @param {object | null | undefined} lastResponse
 * @param {{ mergeItemIntoWishlist: (item: object) => void, removeWishlistItemFromAgent?: (id: string) => void, isInWishlist?: (id: string) => boolean, wishlistItems?: object[] }} wishlist
 * @param {{ candidateProducts?: object[] }} [opts]
 * @returns {{ handled: boolean, toast: string | null }}
 */
export function applyWishlistFromAgentResponse(lastResponse, wishlist, opts = {}) {
  const mergeItemIntoWishlist = wishlist?.mergeItemIntoWishlist;
  // State only, like the merge: the agent has already removed the item
  // server-side, so the manual `removeFromWishlist` (a DELETE to the
  // wishlist API) is the wrong tool here — it would be a second delete.
  const removeWishlistItemFromAgent = wishlist?.removeWishlistItemFromAgent;
  if (typeof mergeItemIntoWishlist !== "function") {
    return { handled: false, toast: null };
  }

  // `action` is last-writer-wins across the turn's tools, so a combined turn
  // (add_to_wishlist then search_products) rewrites it to PRODUCT_LIST. The
  // dedicated `wishlist_mutation` stash survives that and is preferred.
  const mutation = lastResponse?.wishlist_mutation;
  const action =
    mutation != null && typeof mutation === "object" && !Array.isArray(mutation)
      ? mutation
      : lastResponse?.action;
  const conversationText = lastResponse?.conversation_text || "";
  const products = Array.isArray(lastResponse?.products)
    ? lastResponse.products
    : [];
  const candidateProducts =
    Array.isArray(opts.candidateProducts) && opts.candidateProducts.length
      ? opts.candidateProducts
      : products;

  let handled = false;
  let toast = null;

  // What this turn newly saved, judged against the wishlist as it stood when
  // the turn began — the difference between "Added" (fly + toast) and "it
  // was already there" (toast only). `isInWishlist` reads render-time state,
  // so the merges below do not affect it.
  const isInWishlist =
    typeof wishlist?.isInWishlist === "function" ? wishlist.isInWishlist : null;
  const newlySaved = [];
  const noteIfNew = (item) => {
    const id = itemId(item);
    if (id == null) return;
    if (isInWishlist && isInWishlist(id)) return;
    if (newlySaved.some((x) => itemId(x) === id)) return;
    newlySaved.push(item);
  };
  let removedItem = null;

  if (action && typeof action === "object") {
    if (action.type === "ADD_TO_WISHLIST" && action.status === "SUCCESS") {
      // The product the tool acted on, when the response names it. It is the
      // one to announce: `wishlist_data` may be the whole list, and on an
      // un-hydrated wishlist every entry in it would otherwise look new.
      const named =
        action.product && (action.product.id ?? action.article_id)
          ? action.product.id
            ? action.product
            : {
                ...action.product,
                id: action.article_id,
                article_id: action.article_id,
              }
          : null;

      if (Array.isArray(action.wishlist_data) && action.wishlist_data.length > 0) {
        action.wishlist_data.forEach((item) => mergeItemIntoWishlist(item));
        if (named) noteIfNew(named);
        else action.wishlist_data.forEach(noteIfNew);
        handled = true;
        toast = "Added to wishlist";
      } else if (named) {
        mergeItemIntoWishlist(named);
        noteIfNew(named);
        handled = true;
        toast = "Added to wishlist";
      } else {
        stampWishlistAgentSync();
      }
    } else if (action.type === "VIEW_WISHLIST") {
      if (Array.isArray(action.wishlist_data) && action.wishlist_data.length > 0) {
        action.wishlist_data.forEach((item) => mergeItemIntoWishlist(item));
      }
      handled = true;
      stampWishlistAgentSync();
    } else if (action.type === "REMOVE_FROM_WISHLIST" && action.status === "SUCCESS") {
      if (action.article_id && typeof removeWishlistItemFromAgent === "function") {
        const rid = String(action.article_id);
        removedItem =
          (Array.isArray(wishlist?.wishlistItems)
            ? wishlist.wishlistItems.find((i) => itemId(i) === rid)
            : null) ||
          (action.product && typeof action.product === "object" ? action.product : null);
        removeWishlistItemFromAgent(rid);
        handled = true;
        toast = "Removed from wishlist";
      } else if (Array.isArray(action.wishlist_data)) {
        action.wishlist_data.forEach((item) => mergeItemIntoWishlist(item));
        handled = true;
        toast = "Removed from wishlist";
      }
      stampWishlistAgentSync();
    }
  }

  if (!handled && lastResponse?.wishlist != null) {
    const rawWl = lastResponse.wishlist;
    if (
      rawWl != null &&
      typeof rawWl === "object" &&
      Array.isArray(rawWl.items) &&
      rawWl.items.length > 0
    ) {
      rawWl.items.forEach((item) => mergeItemIntoWishlist(item));
      rawWl.items.forEach(noteIfNew);
      handled = true;
      toast = "Added to wishlist";
      stampWishlistAgentSync();
    }
  }

  if (!handled && conversationText) {
    const wishlistAddPattern =
      /added.*wishlist|wishlist.*added|saved.*wishlist|already on your wishlist|i['']ve added.*wishlist|on your wishlist/i;
    if (wishlistAddPattern.test(conversationText) && candidateProducts.length > 0) {
      mergeItemIntoWishlist(candidateProducts[0]);
      noteIfNew(candidateProducts[0]);
      handled = true;
      toast = "Added to wishlist";
      stampWishlistAgentSync();
    }

    const wishlistViewPattern =
      /here['']s what['']s on your wishlist|your wishlist|what['']s on your wishlist|items? (saved|in) (your )?wishlist|in your wishlist|on your wishlist|wishlist.*item|saved.*wishlist/i;
    if (wishlistViewPattern.test(conversationText) && candidateProducts.length > 0) {
      candidateProducts.forEach((p) =>
        mergeItemIntoWishlist({
          id: p.id ?? p.article_id,
          article_id: p.article_id ?? p.id,
          title: p.title ?? p.name ?? "",
          name: p.title ?? p.name ?? "",
          price: p.price ?? p.price_info?.price ?? 0,
          image: p.image ?? (Array.isArray(p.images) ? p.images[0] : ""),
          image_uri: p.image_uri ?? p.image,
        })
      );
      handled = true;
      stampWishlistAgentSync();
    }
  }

  if (handled && toast) {
    stampWishlistAgentSync();
  }

  announce(toast, newlySaved, removedItem);

  return { handled, toast };
}

function itemId(item) {
  if (!item || typeof item !== "object") return null;
  const raw = item.id ?? item.product_id ?? item.article_id;
  return raw != null && raw !== "" ? String(raw) : null;
}

/**
 * Tell the shopper what the agent did: the site toast, and for a fresh save
 * the ghost flying from the assistant into the wishlist heart. Ids match the
 * manual paths ("wishlist-add"), so a turn that reaches here twice collapses
 * into one toast, and flyToBag de-duplicates the flight by product.
 */
function announce(toast, newlySaved, removedItem) {
  if (toast === "Added to wishlist") {
    if (newlySaved.length > 0) {
      const first = newlySaved[0];
      flyToBag("wishlist", {
        from: "agent",
        image: productImageOf(first),
        key: itemId(first) || "agent",
      });
      showToast({ id: "wishlist-add", title: "Added to wishlist", products: newlySaved });
    } else {
      showToast({
        id: "wishlist-add",
        title: "Already in your wishlist",
        severity: "info",
      });
    }
  } else if (toast === "Removed from wishlist") {
    showToast({
      id: "wishlist-remove",
      title: "Removed from wishlist",
      products: removedItem ? [removedItem] : [],
    });
  }
}
